import React, { useEffect, useState } from 'react';
import { MenuList, MenuListItem, MenuListItemText, MenuListLabel } from '../../../../../MenuList';
import { Dropdown } from '../../../../../Dropdown';
import { Checkbox } from '../../../../../Checkbox';
import isEqual from 'react-fast-compare';
import { PermanentFilterButton } from '../../view/PermanentFilterButton';

interface MultiCheckboxProps<T> {
  label: string;
  options: { label: string; value: T }[];
  value: T[] | undefined;
  onChange(values: T[] | undefined): void;
  dataTest?: string;
}

export function MultiCheckbox<T>({ options, onChange, value, label, dataTest }: MultiCheckboxProps<T>) {
  const [localValue, setLocalValue] = useState<T[] | undefined>();

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const hasValue = !!value?.length;
  const selectedLabels = hasValue
    ? value.map((it) => options.find((option) => option.value === it)?.label ?? 'UNDEFINED')
    : undefined;

  const handleChange = (newValue: T) => {
    setLocalValue((prevValue) => {
      if ((prevValue ?? []).includes(newValue)) {
        return prevValue?.filter((it) => it !== newValue);
      } else {
        return [...(prevValue ?? []), newValue];
      }
    });
  };

  return (
    <Dropdown
      ButtonItem={() => (
        <PermanentFilterButton
          label={label}
          valueLabel={
            !!selectedLabels?.length
              ? `${selectedLabels[0] ?? ''} ${selectedLabels?.length > 1 ? `+${selectedLabels.length - 1}` : ''}`
              : undefined
          }
          dataTest={dataTest ?? 'permanent-filter-multi-checkbox'}
          onDelete={() => onChange(undefined)}
        />
      )}
      onClose={() => {
        if (!isEqual(localValue, value)) {
          onChange(localValue);
        }
      }}
    >
      <MenuList>
        <MenuListLabel>{label}</MenuListLabel>
        {options.map((item) => {
          const isChecked = localValue?.length ? localValue?.includes(item.value) : false;
          return (
            <MenuListItem
              key={typeof item.value === 'string' ? item.value : (item.label ?? '')}
              tabIndex={-1}
              onClick={(e) => {
                handleChange(item.value);
                e.preventDefault?.();
                e.stopPropagation?.();
              }}
            >
              <Checkbox checked={isChecked}>
                <MenuListItemText primary={item.label} />
              </Checkbox>
            </MenuListItem>
          );
        })}
      </MenuList>
    </Dropdown>
  );
}
